import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { type RootState } from "src/common/rootState.type";
import {
  type PackageSize,
  getFbaFee,
  isFrozenFoodCategory,
} from "@cielo-blu-team/amazon-fee-lib-v2";

/**
 * 商品の梱包寸法・重量から `PackageSize` を組み立てる。
 * いずれかが未設定 / 0以下 / 数値変換失敗の場合は undefined を返す。
 */
const buildPackageSize = (
  length: number | undefined,
  width: number | undefined,
  height: number | undefined,
  weight: number | undefined
): PackageSize | undefined => {
  const l = Number(length);
  const w = Number(width);
  const h = Number(height);
  const wg = Number(weight);
  if (
    !Number.isFinite(l) ||
    !Number.isFinite(w) ||
    !Number.isFinite(h) ||
    !Number.isFinite(wg) ||
    l <= 0 ||
    w <= 0 ||
    h <= 0 ||
    wg <= 0
  ) {
    return undefined;
  }
  return { length: l, width: w, height: h, weight: wg };
};

/**
 * FBA 配送代行手数料をクライアント側で算出するカスタムフック。
 *
 * - Redux の `feePatterns.fbaFee` と product の梱包寸法をもとに `getFbaFee` を呼ぶ
 * - 冷凍食品カテゴリ（categoryTree に冷凍食品 ID を含む）の商品では freezingFee が加算される
 * - product 変更時は actPrice で初期計算
 * - 価格変動時は `updateBySellPrice` で再計算
 *
 * 戻り値は `fbaFee`（通常 FBA 手数料）と `freezingFee`（冷凍食品手数料）を分けて返す。
 * 利益計算には両者の合計を使う想定。
 */
export const useFbaFee = (): {
  fbaFee: number | undefined;
  freezingFee: number | undefined;
  updateBySellPrice: (sellPrice: number) => void;
} => {
  const { product } = useSelector((state: RootState) => state.top);
  const fbaFeeContent = useSelector(
    (state: RootState) => state.feePatterns.fbaFee
  );

  const [fbaFee, setFbaFee] = useState<number | undefined>(undefined);
  const [freezingFee, setFreezingFee] = useState<number | undefined>(undefined);

  /** 価格を指定して計算し、戻り値の内訳を返す */
  const calc = (
    sellPrice: number | undefined
  ): { fbaFee: number; freezingFee: number } | undefined => {
    if (!fbaFeeContent || !product) return undefined;
    const price = Number(sellPrice);
    if (!Number.isFinite(price) || price <= 0) return undefined;

    const packageSize = buildPackageSize(
      product.packageLength,
      product.packageWidth,
      product.packageHeight,
      product.packageWeight
    );
    if (!packageSize) return undefined;

    const isFrozen = isFrozenFoodCategory(
      product.categoryTree?.map((c) => c.catId)
    );

    const result = getFbaFee(price, packageSize, fbaFeeContent, isFrozen);
    if (result === null) return undefined;

    return { fbaFee: result.fbaFee, freezingFee: result.freezingFee };
  };

  /** 計算結果を State に反映（undefined のときは両方クリア） */
  const apply = (
    result: { fbaFee: number; freezingFee: number } | undefined
  ): void => {
    setFbaFee(result?.fbaFee);
    setFreezingFee(result?.freezingFee);
  };

  // product / fbaFeeContent 変更時に actPrice で再計算
  useEffect(() => {
    apply(calc(product?.actPrice));
    // calc は product / fbaFeeContent に依存するが、関数自体をメモ化していないため依存配列には含めない
  }, [product, fbaFeeContent]);

  const updateBySellPrice = (sellPrice: number): void => {
    apply(calc(sellPrice));
  };

  return {
    fbaFee,
    freezingFee,
    updateBySellPrice,
  };
};
